import { CloseOutlined } from '@ant-design/icons'
import type { ColumnDef } from '@tanstack/react-table'
import { Popconfirm } from 'antd'

import QuantityToggler from '@/components/Cart/QuantityToggler/QuantityToggler'
import UpdatedAtTooltip from '@/components/Feed/UpdatedAtTooltip/UpdatedAtTooltip'
import OrderStatus from '@/components/Orders/OrderStatus/OrderStatus'
import { ORDER_PRODUCT_STATUSES } from '@/constants'
import { OrderInterface, OrderProductModel, OrderShortType } from '@/types'
import { trimTrailingZeros } from '@/utils/format/trimTrailingZeros'
import { formatPriceWithVat } from '@/utils/price/vat'
import getProductKey from '@/utils/products/getProductKey'

import cn from './Order.module.scss'

export const formatProductsFromServer = (
  products: OrderProductModel[]
): OrderProductModel[] => {
  return products
}

type ProductColumnsParams = {
  orderCode: number
  duplicateOrderProductsQuantities: { [x: string]: number }
  handleDeleteOrderProduct: (product: OrderProductModel) => void
  isStaff: boolean
  canEditProducts: boolean
}

export const ORDER_PRODUCT_COLUMN_WIDTHS: Partial<Record<string, string>> = {
  article: 'minmax(110px, 140px)',
  description: 'minmax(220px, 1fr)',
  brand: 'minmax(100px, 140px)',
  editableQuantity: '170px',
  quantity: '110px',
  status: '150px',
  price: '110px',
  amount: '110px',
  retailPrice: '110px',
  retailPriceSum: '110px',
  store: 'minmax(110px, 160px)',
  deleteProductButton: '56px',
}

const getEditedQuantity = (
  quantities: ProductColumnsParams['duplicateOrderProductsQuantities'],
  record: OrderProductModel
) => quantities[String(getProductKey(record))]

const getDeleteColumn = (
  handleDeleteOrderProduct: ProductColumnsParams['handleDeleteOrderProduct'],
  isStaff: boolean
): ColumnDef<OrderProductModel, unknown> => ({
  id: 'deleteProductButton',
  header: '',
  cell: ({ row }) => (
    <Popconfirm
      title="Удаление товара"
      description="Вы действительно хотите удалить товар из заказа?"
      onConfirm={() => handleDeleteOrderProduct(row.original)}
      icon={null}
      okText="Да"
      cancelText="Нет"
      overlayClassName={
        isStaff
          ? [cn.popconfirm, cn.bluePopconfirmButton].join(' ')
          : cn.popconfirm
      }
      align={{ offset: [-24, -16] }}
    >
      <button
        type="button"
        className={cn.closeOutlinedIconContainer}
        aria-label="Удалить товар из заказа"
      >
        <CloseOutlined />
      </button>
    </Popconfirm>
  ),
})

export const getColumns = ({
  orderCode,
  duplicateOrderProductsQuantities,
  handleDeleteOrderProduct,
  isStaff,
  canEditProducts,
}: ProductColumnsParams): ColumnDef<OrderProductModel, unknown>[] => [
  {
    id: 'article',
    header: 'Артикул',
    cell: ({ row }) => row.original.nomenclatureDto.article,
  },
  {
    id: 'description',
    header: 'Наименование',
    cell: ({ row }) => row.original.nomenclatureDto.description,
  },
  {
    id: 'brand',
    header: 'Бренд',
    cell: ({ row }) => row.original.nomenclatureDto.brandDto?.description,
  },
  canEditProducts
    ? {
        id: 'editableQuantity',
        header: 'Количество',
        cell: ({ row }) => (
          <div className={cn.orderProductQuantityTogglerContainer}>
            <QuantityToggler
              location="profile/orders/:code"
              productQuantity={
                getEditedQuantity(
                  duplicateOrderProductsQuantities,
                  row.original
                ) ??
                row.original.quantity ??
                0
              }
              element={row.original}
              orderCode={orderCode}
              isShowWarning={true}
              itemDesc={
                row.original.nomenclatureDto.unitOfMeasurementDto
                  ?.description || 'шт'
              }
            />
          </div>
        ),
      }
    : {
        id: 'quantity',
        header: 'Количество',
        cell: ({ row }) => row.original.quantity ?? 0,
      },
  {
    id: 'status',
    header: 'Статус',
    cell: ({ row }) => {
      const status = row.original.status
      const statusClassName =
        status === 'REJECTED'
          ? cn.orderProductStatusRejected
          : status === 'READY_FOR_PICKUP'
            ? cn.orderProductStatusReadyForPickup
            : undefined

      return (
        <span className={statusClassName}>
          {status ? ORDER_PRODUCT_STATUSES[status] : '-'}
        </span>
      )
    },
  },
  {
    id: 'price',
    header: 'ОПТ цена',
    cell: ({ row }) => (
      <UpdatedAtTooltip
        updatedAt={row.original.priceUpdatedAt}
        kind="price"
        priceSource={row.original.priceSource}
      >
        {formatPriceWithVat(row.original.price ?? 0)}
      </UpdatedAtTooltip>
    ),
  },
  {
    id: 'amount',
    header: 'ОПТ сумма',
    cell: ({ row }) => {
      const record = row.original
      if (record.status === 'REJECTED') return '-'
      const editedQuantity = getEditedQuantity(
        duplicateOrderProductsQuantities,
        record
      )
      if (editedQuantity)
        return formatPriceWithVat((record.price ?? 0) * editedQuantity)
      return trimTrailingZeros(record.total ?? 0)
    },
  },
  {
    id: 'retailPrice',
    header: 'РРЦ цена',
    cell: ({ row }) => formatPriceWithVat(row.original.retailPrice ?? 0),
  },
  {
    id: 'retailPriceSum',
    header: 'РРЦ сумма',
    cell: ({ row }) => {
      const record = row.original
      if (record.status === 'REJECTED') return '-'
      const editedQuantity = getEditedQuantity(
        duplicateOrderProductsQuantities,
        record
      )
      if (editedQuantity)
        return formatPriceWithVat((record.retailPrice ?? 0) * editedQuantity)
      return formatPriceWithVat(record.totalRetailPrice ?? 0)
    },
  },
  {
    id: 'store',
    header: 'Склад',
    cell: ({ row }) => row.original.storeShortModel?.description ?? '',
  },
  ...(canEditProducts
    ? [getDeleteColumn(handleDeleteOrderProduct, isStaff)]
    : []),
]

export const orderToSupplierColumns = [
  {
    title: 'Статус',
    key: 'orderStatus',
    dataIndex: 'orderStatus',
    render: (
      _: undefined,
      record: OrderInterface['orderModel'] | OrderShortType
    ) => {
      return <OrderStatus order={record} />
    },
  },
  {
    title: 'Код заказа',
    key: 'code',
    dataIndex: 'code',
  },
  {
    title: 'Дата заказа',
    key: 'createdAt',
    dataIndex: 'createdAt',
    render: (
      _: undefined,
      record: OrderInterface['orderModel'] | OrderShortType
    ) => {
      const formattedDate = new Date(record.createdAt).toLocaleDateString(
        'ru-RU'
      )
      return <span>{formattedDate}</span>
    },
  },
  {
    title: 'Автор',
    key: 'author',
    render: (
      _: undefined,
      record: OrderInterface['orderModel'] | OrderShortType
    ) => {
      if ('author' in record) return <span>{record.author}</span>
      return <span>{record.authorShortModel?.description ?? ''}</span>
    },
  },
  {
    title: 'Сумма, BYN',
    key: 'totalPrice',
    dataIndex: 'totalPrice',
  },
  {
    title: 'Договор',
    key: 'contract',
    dataIndex: ['contractShortModel', 'description'],
  },
  {
    title: 'Адрес доставки',
    key: 'deliveryAddress',
    render: (
      _: undefined,
      record: OrderInterface['orderModel'] | OrderShortType
    ) => {
      if ('author' in record) return null
      return <span>{record.deliveryAddress || 'Самовывоз'}</span>
    },
  },
  {
    title: 'Комментарий',
    key: 'comment',
    dataIndex: 'comment',
  },
]
